import { getPaywallAgentModelForPlan } from "@tabbio/utils/agent-models";
import { subscriptionCatalog } from "@tabbio/utils";
import type { AgentModelBrand } from "@tabbio/utils/agent-models";
import type { FeatureKey, PaidPlanTier, PlanSet } from "@tabbio/types";

export type Tier = PaidPlanTier;
export type BillingPeriod = "monthly" | "yearly";
export type PlanMode = PlanSet;
type ComparisonFeatureKey =
  | "comparison.top_ai"
  | "comparison.usage"
  | "comparison.visibility";
type PlusDisplayFeatureKey = "plus.connected_apps";
type PaywallSyntheticFeatureKey = ComparisonFeatureKey | PlusDisplayFeatureKey;
type PaywallFeatureKey = FeatureKey | PaywallSyntheticFeatureKey;

const PLUS_PAYWALL_MODEL = getPaywallAgentModelForPlan("plus");

const PRO_PAYWALL_MODEL = getPaywallAgentModelForPlan("pro");

/**
 * Minimal translator shape so this pure module never imports react hooks.
 * Both UpgradeContent variants pass the `common`-namespace `t` from
 * useTranslation so every label renders in the active locale.
 */
export type UpgradeTranslator = (
  key: string,
  options?: Record<string, unknown>,
) => string;

export type FeatureCell =
  | boolean
  | {
      title: string;
      subtitle?: string;
      modelBrand?: AgentModelBrand;
    };

export interface FeatureRow {
  key: string;
  label: string;
  modelBrand?: AgentModelBrand;
  free: FeatureCell;
  paid: FeatureCell;
}

/**
 * Individual paywall rows are authored to the pricing spec rather than derived
 * from the entitlement catalog, so the Plus/Pro screens read exactly as
 * designed. Employer rows still come from the catalog below.
 */
type SpecRow = {
  key: string;
  labelKey: string;
  free: FeatureCell | { titleKey: string; subtitleKey?: string };
  paid: FeatureCell | { titleKey: string; subtitleKey?: string };
};

const INDIVIDUAL_SPEC_ROWS: Record<Tier, SpecRow[]> = {
  plus: [
    { key: "everything", labelKey: "everythingInFree", free: true, paid: true },
    { key: "smarter_ai", labelKey: "smarterAi", free: false, paid: true },
    { key: "tailored_cvs", labelKey: "tailoredCvs", free: false, paid: true },
    {
      key: "unlimited_autofill",
      labelKey: "unlimitedAutofill",
      free: { titleKey: "autofill_free" },
      paid: { titleKey: "autofill_plus" },
    },
    { key: "cover_letters", labelKey: "coverLetters", free: false, paid: true },
    { key: "who_viewed", labelKey: "seeWhoViewed", free: false, paid: true },
    {
      key: "download_format",
      labelKey: "anyDownloadFormat",
      free: false,
      paid: true,
    },
    {
      key: "custom_branding",
      labelKey: "customBranding",
      free: false,
      paid: true,
    },
    { key: "connect_apps", labelKey: "connectApps", free: false, paid: true },
  ],
  pro: [
    { key: "everything", labelKey: "everythingInPlus", free: true, paid: true },
    { key: "finds_jobs", labelKey: "findsJobs", free: false, paid: true },
    { key: "automations", labelKey: "automations", free: false, paid: true },
    {
      key: "extended_memory",
      labelKey: "extendedAgentMemory",
      free: false,
      paid: true,
    },
  ],
};

export interface TierConfig {
  title: string;
  subtitle: string;
  ctaMonthly: string;
  ctaYearly: string;
  freeColumnLabel: string;
  paidColumnLabel: string;
  monthlyPrice: string;
  yearlyPrice: string;
  features: FeatureRow[];
}

const FEATURE_ORDER: Record<Tier, Record<PlanMode, PaywallFeatureKey[]>> = {
  plus: {
    individual: [
      "individual.cv_writer",
      "individual.cover_letter",
      "individual.application_packet",
      "plus.connected_apps",
      "individual.download_any_format",
      "individual.custom_branding",
      "individual.sources_analytics",
    ],
    employer: [
      "employer.hiring_pages",
      "employer.job_posts",
      "employer.private_contact_visibility",
      "employer.outreach",
      "employer.candidate_ranking",
      "employer.jd_generator",
      "employer.team_analytics",
    ],
  },
  pro: {
    individual: [
      "individual.autopilot_career_agent",
      "individual.automations",
      "individual.interview_prep",
      "individual.salary_insights",
      "individual.recruiter_preview",
      "comparison.top_ai",
      "comparison.usage",
      "individual.extended_memory",
    ],
    employer: [
      "employer.autopilot_hiring",
      "employer.bulk_screening",
      "employer.talent_insights",
      "employer.extended_memory",
      "employer.job_visibility_boost",
      "employer.career_fair_qr_analytics",
      "employer.priority_support",
    ],
  },
};

/**
 * i18n keys (under `upgrade.features.*`) for every catalog feature the paywall
 * lists. The catalog labels in @tabbio/utils are English-only, so all
 * user-visible labels route through the locale files instead.
 */
const FEATURE_LABEL_KEYS: Partial<Record<FeatureKey, string>> = {
  "individual.cv_writer": "smarterAiWriting",
  "individual.cover_letter": "coverLetters",
  "individual.application_packet": "applicationPackets",
  "individual.application_autofill": "unlimitedAutofill",
  "individual.download_any_format": "downloadAnyFormat",
  "individual.custom_branding": "customBranding",
  "individual.sources_analytics": "sourcesAnalytics",
  "individual.higher_visibility": "higherVisibility",
  "individual.autopilot_career_agent": "careerAgent",
  "individual.automations": "novaAutomations",
  "individual.interview_prep": "interviewPrep",
  "individual.salary_insights": "salaryInsights",
  "individual.recruiter_preview": "recruiterPreview",
  "individual.extended_memory": "extendedAgentMemory",
  "employer.hiring_pages": "hiringPages",
  "employer.job_posts": "jobPosts",
  "employer.private_contact_visibility": "privateContactVisibility",
  "employer.outreach": "recruiterOutreach",
  "employer.candidate_ranking": "aiCandidateRanking",
  "employer.jd_generator": "jdGenerator",
  "employer.team_analytics": "teamAnalytics",
  "employer.autopilot_hiring": "autopilotHiring",
  "employer.bulk_screening": "bulkScreening",
  "employer.talent_insights": "talentInsights",
  "employer.extended_memory": "extendedAgentMemory",
  "employer.job_visibility_boost": "doubleJobVisibility",
  "employer.career_fair_qr_analytics": "careerFairQrAnalytics",
  "employer.priority_support": "prioritySupport",
};

function getPrice(planMode: PlanMode, tier: Tier, billing: BillingPeriod) {
  const tierConfig = subscriptionCatalog.planSets[planMode].tiers[tier];
  const price = tierConfig.billing[billing]?.prices[0];
  if (billing === "yearly" && price?.billedDisplay) {
    return price.billedDisplay;
  }
  return price?.display ?? tierConfig.billing.monthly?.prices[0]?.display ?? "";
}

function getComparisonBaseTier(tier: Tier) {
  return tier === "plus" ? "free" : "plus";
}

function isCatalogFeatureKey(key: PaywallFeatureKey): key is FeatureKey {
  return !key.startsWith("comparison.") && !key.startsWith("plus.");
}

function getFeatureLabel(featureKey: FeatureKey, t: UpgradeTranslator) {
  const labelKey = FEATURE_LABEL_KEYS[featureKey];
  if (labelKey) {
    return t(`upgrade.features.${labelKey}`);
  }

  // Fallback for any future paywall row without a translation mapping.
  return subscriptionCatalog.features[featureKey].label;
}

function buildSyntheticRow(
  featureKey: PaywallSyntheticFeatureKey,
  t: UpgradeTranslator,
): FeatureRow {
  switch (featureKey) {
    case "plus.connected_apps":
      return {
        key: featureKey,
        label: t("upgrade.features.connectedApps"),
        free: false,
        paid: true,
      };
    case "comparison.top_ai": {
      return {
        key: featureKey,
        label: t("upgrade.comparison.topAi"),
        free: {
          title: PLUS_PAYWALL_MODEL.displayName,
          subtitle: t("upgrade.comparison.topAiFreeSubtitle"),
          modelBrand: PLUS_PAYWALL_MODEL.brand,
        },
        paid: {
          title: PRO_PAYWALL_MODEL.displayName,
          subtitle: t("upgrade.comparison.topAiPaidSubtitle"),
          modelBrand: PRO_PAYWALL_MODEL.brand,
        },
      };
    }
    case "comparison.usage":
      return {
        key: featureKey,
        label: t("upgrade.comparison.usage"),
        free: { title: t("upgrade.comparison.usageFree") },
        paid: { title: t("upgrade.comparison.usagePaid") },
      };
    case "comparison.visibility":
      return {
        key: featureKey,
        label: t("upgrade.comparison.visibility"),
        free: { title: t("upgrade.comparison.visibilityFree") },
        paid: { title: t("upgrade.comparison.visibilityPaid") },
      };
  }
}

export function getTierConfigs(
  mode: PlanMode,
  t: UpgradeTranslator,
): Record<Tier, TierConfig> {
  return {
    plus: buildTierConfig(mode, "plus", getComparisonBaseTier("plus"), t),
    pro: buildTierConfig(mode, "pro", getComparisonBaseTier("pro"), t),
  };
}

function getColumnLabel(tier: "free" | "plus" | "pro", t: UpgradeTranslator) {
  if (tier === "free") return t("upgrade.columnFree");
  return tier === "plus" ? t("upgrade.columnPlus") : t("upgrade.columnPro");
}

/**
 * Plus screen: Free vs Plus. Pro screen: Plus vs Pro. Pro additionally carries
 * the AI comparison row; both close on Credits.
 */
function buildIndividualRows(
  tier: Tier,
  baseTier: "free" | "plus",
  t: UpgradeTranslator,
): FeatureRow[] {
  const paidModel = tier === "plus" ? PLUS_PAYWALL_MODEL : PRO_PAYWALL_MODEL;
  const resolveSpecCell = (
    cell: SpecRow["free"] | SpecRow["paid"],
  ): FeatureCell => {
    if (typeof cell === "object" && "titleKey" in cell) {
      return {
        title: t(`upgrade.rows.${cell.titleKey}`),
        subtitle: cell.subtitleKey
          ? t(`upgrade.rows.${cell.subtitleKey}`)
          : undefined,
      };
    }
    return cell;
  };
  const rows: FeatureRow[] = INDIVIDUAL_SPEC_ROWS[tier].map((row) => {
    const isModelRow = row.key === "smarter_ai";
    return {
      key: row.key,
      label: t(`upgrade.rows.${row.labelKey}`, {
        model: paidModel.displayName,
      }),
      modelBrand: isModelRow ? paidModel.brand : undefined,
      free: resolveSpecCell(row.free),
      paid: resolveSpecCell(row.paid),
    };
  });

  if (tier === "pro") {
    rows.push({
      key: "ai",
      label: t("upgrade.comparison.topAi"),
      free: {
        title: PLUS_PAYWALL_MODEL.displayName,
        subtitle: t("upgrade.comparison.topAiFreeSubtitle"),
        modelBrand: PLUS_PAYWALL_MODEL.brand,
      },
      paid: {
        title: paidModel.displayName,
        subtitle: t("upgrade.comparison.topAiPaidSubtitle"),
        modelBrand: paidModel.brand,
      },
    });
  }

  // Monthly figure on top, daily allowance beneath: the combined string is too
  // long for the value column and was truncating on Pro.
  rows.push({
    key: "credits",
    label: t("upgrade.rows.credits"),
    free: {
      title: t(`upgrade.rows.credits_${baseTier}`),
      subtitle: t(`upgrade.rows.creditsDaily_${baseTier}`),
    },
    paid: {
      title: t(`upgrade.rows.credits_${tier}`),
      subtitle: t(`upgrade.rows.creditsDaily_${tier}`),
    },
  });

  return rows;
}

function buildTierConfig(
  mode: PlanMode,
  tier: Tier,
  baseTier: "free" | "plus",
  t: UpgradeTranslator,
): TierConfig {
  const paidPlan = subscriptionCatalog.planSets[mode].tiers[tier];
  const basePlan = subscriptionCatalog.planSets[mode].tiers[baseTier];
  const orderedFeatures = FEATURE_ORDER[tier][mode];
  const tierSuffix = tier === "plus" ? "Plus" : "Pro";
  const ctaWord = t("upgrade.ctaWord");

  return {
    title: t(`upgrade.${mode}.title${tierSuffix}`),
    subtitle: t(`upgrade.${mode}.subtitle${tierSuffix}`),
    ctaMonthly: `${ctaWord} · ${getPrice(mode, tier, "monthly")}`,
    ctaYearly: `${ctaWord} · ${getPrice(mode, tier, "yearly")}`,
    freeColumnLabel: getColumnLabel(baseTier, t),
    paidColumnLabel: getColumnLabel(tier, t),
    monthlyPrice: getPrice(mode, tier, "monthly"),
    yearlyPrice: getPrice(mode, tier, "yearly"),
    features:
      mode === "individual"
        ? buildIndividualRows(tier, baseTier, t)
        : orderedFeatures.map((featureKey) => {
            if (!isCatalogFeatureKey(featureKey)) {
              return buildSyntheticRow(featureKey, t);
            }

            return {
              key: featureKey,
              label: getFeatureLabel(featureKey, t),
              free: basePlan.features.includes(featureKey),
              paid: paidPlan.features.includes(featureKey),
            };
          }),
  };
}
